import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Box, Fade } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Character, SpeechBubble } from '@/components/character';
import { useCharacter } from '@/contexts/CharacterContext';
import { useEffectiveChildMode } from '@/components/utils/useEffectiveChildMode';
import { useActivePersona } from '@/personas/useActivePersona';
import { useChildTokens } from '@/components/child/useChildTokens';
import { speak } from './useSpeak';
import { useThemePreference } from '@/contexts/ThemePreferenceContext';
import { getThemeTokens } from '@/utils/appThemes';

/**
 * EmbeddedCompanion — den INBÄDDADE kompanjonen för barnvyn (ersätter den
 * flytande CharacterCompanion i barnläge). Varje barn-vy monterar exakt EN.
 *
 * Varianter (matchar prototypens layouter, Todej ny ux.html):
 *  - 'home'      → stor character-section (avatar + bubbla bredvid, ev. children
 *                  som mood-check), motsvarar .character-section.
 *  - 'row'       → kompakt rad (avatar + bubbla), motsvarar .screen-char-row.
 *  - 'celebrate' → centrerad stor avatar + bubbla under, för firande-vyn.
 *  - 'create'    → kompakt rad i skapa-wizardens steg (.step-character-row).
 *
 * Beteende: läser vald karaktär + mood + companionSpeech ur CharacterContext.
 * Tap på avataren → mood='happy' i 1200 ms → tillbaka till 'idle' + läser upp
 * bubbeltexten via useSpeak (TTS). Mood/bubbeltext i övrigt sätts av vyn via
 * useCompanionMoment.
 *
 * Gate (invariant: inget barn-läckage): renderar null om inte barnläge ELLER
 * ingen karaktär vald. accent väljer ramfärg (primary/green/amber) ur barntemat.
 */

export type EmbeddedVariant = 'home' | 'row' | 'celebrate' | 'create';

export type EmbeddedCompanionProps = {
  variant: EmbeddedVariant;
  /** Ramens accentfärg. Default 'primary'. */
  accent?: 'primary' | 'green' | 'amber';
  /** Avatarstorlek i px. Default härleds ur variant. */
  size?: number;
  /** Extra innehåll under bubblan (t.ex. DailyMoodCheck på hemskärmen). */
  children?: ReactNode;
};

const defaultSize: Record<EmbeddedVariant, number> = {
  home: 80,
  row: 48,
  create: 48,
  celebrate: 100,
};

const EmbeddedCompanion = ({
  variant,
  accent = 'primary',
  size,
  children,
}: EmbeddedCompanionProps) => {
  const { selectedCharacter, mood, companionSpeech, setMood } = useCharacter();
  const effectiveChildMode = useEffectiveChildMode();
  const profile = useActivePersona();
  const tokens = useChildTokens();
  const { t } = useTranslation();
  const { selectedTheme, isDarkMode } = useThemePreference();
  const tk = getThemeTokens(selectedTheme, isDarkMode);
  const [pulseKey, setPulseKey] = useState(0);
  // Håll timer-id i ref så vi kan rensa vid ny tap OCH vid unmount (annars
  // setMood efter avmontering → React-varning + ev. mood-läckage till nästa vy).
  const moodResetRef = useRef<number | null>(null);

  const hasSpeech = companionSpeech.trim().length > 0;

  // Rensa ev. pågående mood-reset-timer när komponenten avmonteras.
  useEffect(
    () => () => {
      if (moodResetRef.current !== null) {
        window.clearTimeout(moodResetRef.current);
      }
    },
    [],
  );

  const handleTap = useCallback(() => {
    // Visuell feedback körs alltid; happy 1.2s → idle.
    setMood('happy');
    setPulseKey((k) => k + 1);
    if (hasSpeech) {
      speak(companionSpeech);
    }
    // Avbryt tidigare reset (snabba upprepade tap) innan ny schemaläggs.
    if (moodResetRef.current !== null) {
      window.clearTimeout(moodResetRef.current);
    }
    moodResetRef.current = window.setTimeout(() => {
      setMood('idle');
      moodResetRef.current = null;
    }, 1200);
  }, [companionSpeech, hasSpeech, setMood]);

  // Teen-gate (iteration 1): companion är AV som default för ungdom → ingen
  // inbäddad kompanjon renderas. Ligger FÖRE barn-gaten nedan; barn/vuxen
  // påverkas inte. (Inget nytt fält på CharacterContext.)
  if (profile.persona === 'teen' && profile.features.companion.defaultMode === 'off') {
    return null;
  }

  // Gate: aldrig i vuxenläge, aldrig utan vald karaktär.
  if (!effectiveChildMode || selectedCharacter === null) {
    return null;
  }

  const accentColor = tk.primary;
  const accentBg = `${tk.primary}15`;

  const avatarSize = size ?? defaultSize[variant];
  const isCelebrate = variant === 'celebrate';

  const avatarButton = (
    <Box
      component="button"
      type="button"
      onClick={handleTap}
      aria-label={t('child.companion.tapToHear', 'Tryck för att höra igen') as string}
      sx={{
        flexShrink: 0,
        border: 'none',
        background: 'transparent',
        p: 0,
        borderRadius: '50%',
        cursor: 'pointer',
        display: 'inline-flex',
        transition: 'transform 120ms ease-out',
        '&:active': { transform: 'scale(0.94)' },
        '&:focus-visible': {
          outline: '3px solid',
          outlineColor: accentColor,
          outlineOffset: 4,
        },
        '@media (hover: hover)': { '&:hover': { transform: 'scale(1.04)' } },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          '&:active': { transform: 'none' },
          '&:hover': { transform: 'none' },
        },
      }}
    >
      <Character character={selectedCharacter} mood={mood} size={avatarSize} decorative />
    </Box>
  );

  const bubble = (
    <Fade in={hasSpeech} timeout={{ enter: 240, exit: 160 }} unmountOnExit>
      <Box
        key={pulseKey}
        sx={{
          flex: variant === 'home' ? 1 : 'unset',
          minWidth: 0,
          '@keyframes bubblePulse': {
            '0%': { transform: 'scale(1)' },
            '40%': { transform: 'scale(1.05)' },
            '100%': { transform: 'scale(1)' },
          },
          animation: pulseKey > 0 ? 'bubblePulse 340ms ease-out' : 'none',
          transformOrigin: isCelebrate ? 'center top' : 'left center',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        <SpeechBubble
          text={companionSpeech}
          pointer={isCelebrate ? 'top' : 'left'}
          ariaLive="polite"
          speakable
        />
      </Box>
    </Fade>
  );

  return (
    <Box
      data-testid="embedded-companion"
      data-variant={variant}
      sx={{
        display: 'flex',
        flexDirection: isCelebrate ? 'column' : 'row',
        alignItems: 'center',
        gap: 1.5,
        bgcolor: accentBg,
        border: '2px solid',
        borderColor: accentColor,
        borderRadius: '20px',
        p: variant === 'home' ? 2 : 1.75,
        ...(isCelebrate && { textAlign: 'center', bgcolor: 'transparent', border: 'none', p: 0 }),
      }}
    >
      {avatarButton}
      <Box sx={{ flex: isCelebrate ? 'unset' : 1, minWidth: 0, width: '100%' }}>
        {bubble}
        {children}
      </Box>
    </Box>
  );
};

export default EmbeddedCompanion;
